import { lazy, Suspense, useState } from 'react';
import { Plus } from 'lucide-react';
import type { AiLesson } from '@ready/shared';
import type { ContentOwner } from '@/lib/api';
import { Button, ErrorText, Field, Input, Textarea } from '@/components/ui';
import {
  AiPanel,
  DraftingNote,
  SourceFields,
  emptySource,
  sourceInput,
  useAiDraft,
  type SourceValue,
} from './ai-assist';

const DraftPreview = lazy(() => import('./draft-preview'));

/**
 * Drafts a lesson's text (Markdown and callouts) from its title, the course
 * outline, the author's instructions and optional source material. The draft
 * is previewed, then added to the editor as blocks to edit and save.
 */
export function LessonAssist({
  lessonId,
  owner,
  dirty,
  hasText,
  onInsert,
  onClose,
}: {
  lessonId: string;
  owner: ContentOwner;
  /** Unsaved edits aren't seen by the draft; it works from the saved lesson. */
  dirty: boolean;
  hasText: boolean;
  onInsert: (draft: AiLesson) => void;
  onClose: () => void;
}) {
  const [instructions, setInstructions] = useState('');
  const [minutes, setMinutes] = useState(15);
  const [source, setSource] = useState<SourceValue>(emptySource);
  const ai = useAiDraft('lesson');
  const draft = ai.draft?.lesson ?? null;

  return (
    <AiPanel title="Write this lesson with AI" onClose={onClose}>
      {!draft && (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            ai.start({
              kind: 'lesson',
              lessonId,
              minutes,
              ...(instructions.trim() ? { instructions: instructions.trim() } : {}),
              ...sourceInput(source),
            });
          }}
        >
          <p className="text-sm text-muted-foreground">
            The draft uses the lesson's title and summary and the course outline
            {hasText ? ', and continues the text already in the lesson' : ''}.
            {dirty ? ' Save first so it sees your latest changes.' : ''}
          </p>
          <Field
            label="Instructions (optional)"
            htmlFor="ai-instructions"
            hint="What to cover, examples to use, tone, the activity you want…"
          >
            <Textarea
              id="ai-instructions"
              rows={3}
              maxLength={4000}
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
            />
          </Field>
          <Field label="Length (minutes)" htmlFor="ai-minutes">
            <Input
              id="ai-minutes"
              type="number"
              min={3}
              max={120}
              className="w-28"
              value={minutes}
              onChange={(e) => setMinutes(Math.min(120, Math.max(3, Number(e.target.value) || 3)))}
            />
          </Field>
          <SourceFields id="ai-lesson" owner={owner} value={source} onChange={setSource} />
          <Button type="submit" disabled={ai.drafting}>
            {ai.drafting ? 'Drafting…' : 'Draft lesson'}
          </Button>
          <DraftingNote active={ai.drafting} />
          <ErrorText error={ai.error} />
        </form>
      )}

      {draft && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {draft.blocks.length} blocks, about {draft.estimatedMinutes} minutes. Add them to the
            lesson to edit, then save.
          </p>
          <div className="max-h-[60vh] overflow-y-auto rounded-lg border border-border p-4">
            <Suspense fallback={<p className="text-sm text-muted-foreground">Loading preview…</p>}>
              <DraftPreview blocks={draft.blocks} />
            </Suspense>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={() => {
                onInsert(draft);
                onClose();
              }}
            >
              <Plus className="size-4" aria-hidden />
              Add to lesson
            </Button>
            <Button type="button" variant="ghost" onClick={ai.reset}>
              Try again
            </Button>
          </div>
        </div>
      )}
    </AiPanel>
  );
}
